import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";

export function useSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("id,company,phone,email,addr,delivery_note,tax_rate,invoice_seq")
        .eq("id", 1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row) => {
      // Settings writes are admin-only (`admin_write`), while reads are open to
      // staff — so a staff user sees the form filled in and gets a clear refusal
      // on save rather than a silent no-op.
      const { data, error } = await supabase
        .from("settings")
        .update(row)
        .eq("id", 1)
        .select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) {
        throw new Error("Nothing was saved. Changing company settings requires an admin account.");
      }
      return data[0];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }),
  });
}

/** Everyone with a profile. Staff may read this; only admins may change it. */
export function useProfiles() {
  return useQuery({
    queryKey: ["profiles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,name,role,perms,created_at")
        .order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch, currentUserId, profiles }) => {
      // Refuse to remove the last admin, for the same reason grant-role.mjs
      // does: the panel that fixes roles is itself admin-only, so there would be
      // no way back in from the app.
      if (patch.role && patch.role !== "admin") {
        const admins = profiles.filter((p) => p.role === "admin");
        if (admins.length <= 1 && admins[0]?.id === id) {
          throw new Error(
            "This is the only admin account. Promote someone else first — otherwise " +
              "Books, payroll, settings, and this panel all become unreachable."
          );
        }
      }
      if (patch.role && id === currentUserId && patch.role !== "admin") {
        throw new Error("You cannot remove your own admin access from here.");
      }

      const { data, error } = await supabase.from("profiles").update(patch).eq("id", id).select("id");
      if (error) throw new Error(error.message);
      if (!data?.length) throw new Error("Nothing changed — managing accounts requires an admin.");
      return data[0];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profiles"] }),
  });
}

/**
 * Change the signed-in user's own password.
 *
 * Supabase will happily update a password from an existing session without
 * asking for the old one, which would let anyone at an unattended logged-in
 * machine lock the owner out. Re-authenticating first closes that.
 */
export async function changeOwnPassword({ email, currentPassword, newPassword }) {
  const { error: reauth } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  });
  if (reauth) throw new Error("Current password is wrong.");

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw new Error(error.message);
}
